import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import * as Yup from "yup";
import imageCompression from "browser-image-compression";
import { enqueueSnackbar } from "notistack";
import { FiTrash2, FiMapPin, FiPlus, FiX } from "react-icons/fi";
import { api } from "../../services/api";
import { PageTitle } from "../../components/PageTitle";
import { AuthContext } from "../../contexts/AuthContext";
import { FormButton } from "../../components/FormButton";
import { SelectSingle } from "../../components/SelectSingle";
import { SelectSearch } from "../../components/SelectSearch";
import { DateHourPicker } from "../../components/DateHourPicker";
import { Loading } from "../../components/Loading";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ptBR", ptBR);

interface SelectOption {
  id: string;
  name: string;
  value?: string;
  label?: string;
}

interface ImageFile {
  preview: string;
  file: File;
}

interface ExistingImage {
  id: string;
  file_name: string;
}

interface FormDeliveryReport {
  costumer_id: string;
  user_id: string;
  begin: string;
  end: string;
  observation: string;
  technician_id: string;
  latitude: number | null;
  longitude: number | null;
}

export function DeliveryReportCreate(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [reportData, setReportData] = useState<any>(null);

  const [users, setUsers] = useState<SelectOption[]>([]);
  const [deliveryService, setDeliveryService] = useState<SelectOption | null>(null);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [images, setImages] = useState<ImageFile[]>([]);
  const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
  const [gpsLocation, setGpsLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [gpsError, setGpsError] = useState<string>("");

  const validationSchema = Yup.object().shape({
    costumer_id: Yup.string().required("Selecione um cliente"),
    begin: Yup.string().required("Selecione a data de início"),
    end: Yup.string().required("Selecione a data de término"),
    observation: Yup.string(),
    technician_id: Yup.string().required("Selecione um técnico"),
  });

  useEffect(() => {
    async function loadData() {
      try {
        setLoadingPage(true);

        // Verificar se está em modo de edição (rota contendo 'edit')
        const isEdit = window.location.pathname.includes('/edit/');
        setIsEditMode(isEdit);

        const [usersRes, servicesRes] = await Promise.all([
          api.get("/users?status=A"),
          api.get("/services"),
        ]);

        setUsers(usersRes.data);

        // Encontrar o serviço "Entrega"
        const delivery = servicesRes.data.find(
          (service: SelectOption) => service.name.toLowerCase() === "entrega"
        );
        setDeliveryService(delivery || null);

        // Se está em modo de edição, carregar os dados do relatório
        if (isEdit && id) {
          const reportRes = await api.get(`/techVisitReport/${id}`);
          const report = reportRes.data;
          setReportData(report);
          setStartDate(new Date(report.begin));
          setEndDate(new Date(report.end));
          setExistingImages(report.images || []);

          if (report.latitude && report.longitude) {
            setGpsLocation({
              latitude: parseFloat(report.latitude),
              longitude: parseFloat(report.longitude),
            });
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        enqueueSnackbar("Erro ao carregar dados", { variant: "error" });
      } finally {
        setLoadingPage(false);
      }
    }

    loadData();
  }, [id]);

  useEffect(() => {
    // Capturar localização GPS ao montar o componente
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setGpsError("");
        },
        (error) => {
          console.error("Erro ao capturar GPS:", error);
          setGpsError("Não foi possível capturar a localização");
          enqueueSnackbar("Não foi possível capturar a localização GPS", { variant: "warning" });
        }
      );
    } else {
      setGpsError("GPS não disponível neste navegador");
    }
  }, []);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      };

      try {
        const compressedFiles = await Promise.all(
          files.map(async (file) => {
            const compressedFile = await imageCompression(file, compressionOptions);
            return {
              preview: URL.createObjectURL(compressedFile),
              file: new File([compressedFile], file.name, { type: file.type }),
            };
          })
        );

        setImages((prev) => [...prev, ...compressedFiles]);
      } catch (error) {
        console.error("Erro ao comprimir imagens:", error);
        enqueueSnackbar("Erro ao processar imagens", { variant: "error" });
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = async (imageId: string) => {
    try {
      await api.delete(`/techVisitReport/images/${imageId}`);
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
      enqueueSnackbar("Imagem removida com sucesso!", { variant: "success" });
    } catch (error) {
      console.error("Erro ao remover imagem:", error);
      enqueueSnackbar("Erro ao remover imagem", { variant: "error" });
    }
  };

  const handleSubmit = async (values: FormDeliveryReport) => {
    try {
      setLoading(true);

      if (!deliveryService) {
        enqueueSnackbar("Serviço de entrega não encontrado", { variant: "error" });
        setLoading(false);
        return;
      }

      if (!values.costumer_id) {
        enqueueSnackbar("Selecione um cliente", { variant: "error" });
        setLoading(false);
        return;
      }

      if (!values.technician_id) {
        enqueueSnackbar("Selecione um entregador", { variant: "error" });
        setLoading(false);
        return;
      }

      if (isEditMode && id) {
        // Modo de edição - atualizar relatório existente
        const updateData = {
          costumer_id: values.costumer_id,
          begin: values.begin,
          end: values.end,
          observation: values.observation,
          technician_id: values.technician_id,
          latitude: gpsLocation?.latitude || null,
          longitude: gpsLocation?.longitude || null,
        };

        await api.put(`/techVisitReport/${id}`, updateData);

        // Upload de novas imagens se houver
        if (images.length > 0) {
          const formData = new FormData();
          images.forEach((img) => {
            formData.append("files", img.file);
          });

          await api.post(`/techVisitReport/images/${id}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }

        enqueueSnackbar("Relatório de entrega atualizado com sucesso!", { variant: "success" });
        navigate("/tech-visit-reports");
      } else {
        // Modo de criação - criar novo relatório
        const reportData = {
          costumer_id: values.costumer_id,
          user_id: user.id,
          begin: values.begin,
          end: values.end,
          observation: values.observation,
          pending: false,
          sent: false,
          type: "delivery",
          latitude: gpsLocation?.latitude || null,
          longitude: gpsLocation?.longitude || null,
          services: [{ service_id: deliveryService.id }],
          sectors: [],
          responsible_id: null,
          technician_id: values.technician_id,
        };

        const response = await api.post("/techVisitReport/unified", reportData);
        const { report_id } = response.data;

        // Upload de imagens se houver
        if (images.length > 0) {
          const formData = new FormData();
          images.forEach((img) => {
            formData.append("files", img.file);
          });

          await api.post(`/techVisitReport/images/${report_id}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        }

        enqueueSnackbar("Relatório de entrega criado com sucesso!", { variant: "success" });
        navigate("/tech-visit-reports");
      }
    } catch (error: any) {
      console.error("Erro ao criar relatório:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Erro ao criar relatório de entrega",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingPage) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      <PageTitle title={isEditMode ? "Editar Relatório de Entrega" : "Novo Relatório de Entrega"} />

      <div className="flex flex-col w-full max-w-5xl mx-auto px-4 py-6">
        <Formik
          initialValues={{
            costumer_id: reportData?.costumer_id || id || "",
            user_id: user.id,
            begin: reportData?.begin || startDate.toISOString(),
            end: reportData?.end || endDate.toISOString(),
            observation: reportData?.observation || "",
            technician_id: reportData?.technician_id || "",
            latitude: gpsLocation?.latitude || null,
            longitude: gpsLocation?.longitude || null,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, setFieldTouched, errors, touched }) => (
            <Form className="flex flex-col gap-6">
              {/* Seção 1: Informações Básicas */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Informações da Entrega
                  </h3>
                </div>

                <div className="p-6 space-y-4">
                  {/* GPS Status */}
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <FiMapPin className={gpsLocation ? "text-green-500" : "text-gray-400"} size={20} />
                    <span className="text-sm">
                      {gpsLocation
                        ? `GPS capturado: ${gpsLocation.latitude.toFixed(6)}, ${gpsLocation.longitude.toFixed(6)}`
                        : gpsError || "Capturando localização..."}
                    </span>
                  </div>

                  {/* Cliente */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Cliente *
                    </label>
                    <SelectSearch
                      name="costumer_id"
                      placeholder="Digite para buscar o cliente..."
                      value={values.costumer_id}
                      onChange={(value) => {
                        setFieldValue("costumer_id", value, true);
                        setFieldTouched("costumer_id", true, false);
                      }}
                      error={errors.costumer_id && touched.costumer_id ? String(errors.costumer_id) : undefined}
                    />
                  </div>

                  {/* Data Início e Fim */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">
                        Data de Início *
                      </label>
                      <DateHourPicker
                        selected={startDate}
                        onChange={(date: Date) => {
                          setStartDate(date);
                          setFieldValue("begin", date.toISOString());
                        }}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        locale="ptBR"
                      />
                      {errors.begin && touched.begin && (
                        <span className="text-red-500 text-sm mt-1">{String(errors.begin)}</span>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">
                        Data de Término *
                      </label>
                      <DateHourPicker
                        selected={endDate}
                        onChange={(date: Date) => {
                          setEndDate(date);
                          setFieldValue("end", date.toISOString());
                        }}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="dd/MM/yyyy HH:mm"
                        locale="ptBR"
                      />
                      {errors.end && touched.end && (
                        <span className="text-red-500 text-sm mt-1">{String(errors.end)}</span>
                      )}
                    </div>
                  </div>

                  {/* Técnico */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      Entregador *
                    </label>
                    <SelectSingle
                      name="technician_id"
                      error={errors.technician_id && touched.technician_id ? String(errors.technician_id) : undefined}
                    >
                      <option value="">Selecione o técnico</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </SelectSingle>
                  </div>
                </div>
              </div>

              {/* Seção 2: Fotos */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Fotos da Entrega
                  </h3>
                </div>

                <div className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {existingImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <button
                          type="button"
                          className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                          onClick={() => window.open(`https://app-luvilimp.s3.amazonaws.com/techVisitReportImages/${img.file_name}`, '_blank')}
                          title="Clique para ampliar"
                        >
                          <img
                            className="w-full h-full object-cover"
                            src={`https://app-luvilimp.s3.amazonaws.com/techVisitReportImages/${img.file_name}`}
                            alt="Foto da entrega"
                          />
                        </button>
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-md"
                          onClick={() => handleRemoveExistingImage(img.id)}
                          title="Remover imagem"
                        >
                          <FiTrash2 size={12} className="text-white" />
                        </button>
                      </div>
                    ))}

                    {images.map((img, index) => (
                      <div key={index} className="relative group">
                        <button
                          type="button"
                          className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#0085BD] transition-colors focus:outline-none focus:ring-2 focus:ring-[#0085BD]"
                          onClick={() => window.open(img.preview, '_blank')}
                          title="Clique para ampliar"
                        >
                          <img
                            className="w-full h-full object-cover"
                            src={img.preview}
                            alt={`Preview ${index + 1}`}
                          />
                        </button>
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-md"
                          onClick={() => handleRemoveImage(index)}
                          title="Remover imagem"
                        >
                          <FiX size={12} className="text-white" />
                        </button>
                      </div>
                    ))}

                    {/* Botão Adicionar Fotos */}
                    <label
                      htmlFor="delivery-images"
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-[#0085BD] hover:border-[#006a96] hover:bg-[#0085BD]/5 transition-all cursor-pointer flex flex-col items-center justify-center gap-0.5 group"
                      title="Adicionar fotos"
                    >
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                        id="delivery-images"
                      />
                      <FiPlus size={20} className="text-[#0085BD] group-hover:scale-110 transition-transform" />
                      <span className="text-[10px] text-[#0085BD] font-medium">Adicionar</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Seção 3: Observações */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Observações
                  </h3>
                </div>

                <div className="p-6">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Notas
                  </label>
                  <textarea
                    value={values.observation}
                    onChange={(e) => setFieldValue("observation", e.target.value)}
                    className="h-[104px] p-4 w-full rounded-xl text-gray-700 bg-gray-50 border border-gray-200 focus:border-[#0085BD] focus:ring-2 focus:ring-[#0085BD]/20 focus:bg-white resize-none transition-all duration-200"
                    placeholder="Digite observações sobre a entrega..."
                  />
                </div>
              </div>

              {/* Botão Finalizar */}
              <FormButton
                title={loading ? "Processando..." : "Finalizar Entrega"}
                type="submit"
                variant="success"
                icon="check"
                disabled={loading}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
